import React, { useState, useCallback } from "react";
import styled from "@emotion/styled";
import { Eta as mmm } from "eta";
import {
  useBuildWithAIMutation,
  type AIBuildRequest,
} from "../../api/pagesApi";

const Eta: any = mmm;

const AIBuilderContainer = styled.div`
  padding: 24px;
  max-width: 1600px;
  margin: 0 auto;
  display: flex;
  gap: 24px;
  height: calc(100vh - 120px);
`;

const LeftPanel = styled.div`
  width: 400px;
  background: white;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  border: 1px solid #e1e5e9;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
`;

const PreviewContainer = styled.div`
  flex: 1;
  background: white;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  border: 1px solid #e1e5e9;
  display: flex;
  flex-direction: column;
`;

const PanelTitle = styled.h2`
  margin: 0 0 16px 0;
  font-size: 18px;
  font-weight: 600;
  color: #333;
`;

const FormSection = styled.div`
  margin-bottom: 24px;
`;

const FormGroup = styled.div`
  margin-bottom: 16px;
`;

const FormLabel = styled.label`
  display: block;
  margin-bottom: 4px;
  font-size: 14px;
  font-weight: 500;
  color: #555;
`;

const FormInput = styled.input`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: #ff6b6b;
  }
`;

const FormSelect = styled.select`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  transition: border-color 0.2s;
  background-color: white;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: #ff6b6b;
  }
`;

const FormTextarea = styled.textarea`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  min-height: 80px;
  resize: vertical;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: #ff6b6b;
  }
`;

const ColorInput = styled.input`
  width: 60px;
  height: 36px;
  padding: 0;
  border: 1px solid #ddd;
  border-radius: 4px;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: #ff6b6b;
  }
`;

const ColorsRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
`;

const VariableGroup = styled.div`
  margin-bottom: 16px;
`;

const PreviewFrame = styled.iframe`
  width: 100%;
  flex: 1;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: white;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
  color: #666;
`;

const ErrorContainer = styled.div`
  padding: 20px;
  background: #fee;
  border: 1px solid #fcc;
  border-radius: 4px;
  color: #c33;
  margin-bottom: 16px;
`;

const GenerateButton = styled.button`
  background: #ff6b6b;
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 6px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  margin-bottom: 16px;
  width: 100%;
  transition: background-color 0.2s;

  &:hover {
    background: #ff5252;
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
`;

const ModifyButton = styled.button`
  background: #38a169;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  margin-top: 8px;
  width: 100%;
  transition: background-color 0.2s;

  &:hover {
    background: #2f855a;
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
`;

const ApplyButton = styled.button`
  background: #4299e1;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  margin-top: 16px;
  width: 100%;
  transition: background-color 0.2s;

  &:hover {
    background: #3182ce;
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
`;

const TabButton = styled.button<{ active: boolean }>`
  background: ${(props) => (props.active ? "#ff6b6b" : "transparent")};
  color: ${(props) => (props.active ? "white" : "#666")};
  border: 1px solid ${(props) => (props.active ? "#ff6b6b" : "#ddd")};
  padding: 8px 16px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  margin-right: 8px;
  margin-bottom: 16px;
  transition: all 0.2s;

  &:hover {
    background: ${(props) => (props.active ? "#ff5252" : "#f5f5f5")};
  }
`;

const TabContainer = styled.div`
  display: flex;
  margin-bottom: 16px;
`;

interface Variable {
  name: string;
  value: string;
}

interface FormData {
  prompt: string;
  title: string;
  description: string;
  primaryColor: string;
  secondaryColor: string;
  backgroundColor: string;
  textColor: string;
  fontFamily: string;
  businessName: string;
  industry: string;
  phone: string;
  email: string;
  address: string;
}

interface AIBuilderProps {
  onBack?: () => void;
}

type ActiveTab = "form" | "variables" | "modify";

// Initialize ETA template engine
const eta = new (Eta as any)({
  tags: ["{{=", "}}"],
  useWith: true,
  autoTrim: false,
});

export const AIBuilder: React.FC<AIBuilderProps> = ({ onBack }) => {
  const [activeTab, setActiveTab] = useState<ActiveTab>("form");
  const [variables, setVariables] = useState<Variable[]>([]);
  const [htmlContent, setHtmlContent] = useState<string>("");
  const [renderedHtml, setRenderedHtml] = useState<string>("");
  const [error, setError] = useState<string | null>(null);
  const [modificationRequest, setModificationRequest] = useState<string>("");
  const [hasVariableChanges, setHasVariableChanges] = useState<boolean>(false);

  const [formData, setFormData] = useState<FormData>({
    prompt: "Generate a modern landing page for...",
    title: "Smart Land Solutions",
    description: "An energetic website focused on fitness and health",
    primaryColor: "#ff6b6b",
    secondaryColor: "#2c3e50",
    backgroundColor: "#ffffff",
    textColor: "#333333",
    fontFamily: "Arial, sans-serif",
    businessName: "Your Business Name",
    industry: "Technology",
    phone: "+1 (555) 123-4567",
    email: "contact@example.com",
    address: "123 Main St, City, State 12345",
  });

  const [buildWithAI, { isLoading }] = useBuildWithAIMutation();

  const getDefaultValueForVariable = useCallback(
    (varName: string): string => {
      const lowerName = varName.toLowerCase();

      // Map common variable names to form data
      const variableMap: Record<string, string> = {
        title: formData.title,
        businessName: formData.businessName,
        description: formData.description,
        primaryColor: formData.primaryColor,
        secondaryColor: formData.secondaryColor,
        backgroundColor: formData.backgroundColor,
        textColor: formData.textColor,
        phone: formData.phone,
        email: formData.email,
        address: formData.address,
        industry: formData.industry,
      };

      // Direct match
      if (variableMap[varName]) {
        return variableMap[varName];
      }

      // Handle nested variables
      if (varName.includes(".")) {
        const [parent, child] = varName.split(".");
        if (parent === "business") {
          switch (child) {
            case "name":
              return formData.businessName;
            case "phone":
              return formData.phone;
            case "email":
              return formData.email;
            case "address":
              return formData.address;
          }
        }
        if (parent === "colors") {
          switch (child) {
            case "primary":
              return formData.primaryColor;
            case "secondary":
              return formData.secondaryColor;
            case "background":
              return formData.backgroundColor;
            case "text":
              return formData.textColor;
          }
        }
      }

      // Pattern matching for unknown variables
      if (lowerName.includes("color") || lowerName.includes("bg")) {
        return formData.primaryColor;
      }

      if (lowerName.includes("title") || lowerName.includes("heading")) {
        return formData.title;
      }

      if (
        lowerName.includes("description") ||
        lowerName.includes("content") ||
        lowerName.includes("text")
      ) {
        return formData.description;
      }

      if (lowerName.includes("company") || lowerName.includes("business")) {
        return formData.businessName;
      }

      if (lowerName.includes("phone")) return formData.phone;
      if (lowerName.includes("email")) return formData.email;
      if (lowerName.includes("address")) return formData.address;

      if (lowerName.includes("cta") || lowerName.includes("button")) {
        return "Get Started";
      }

      // Default fallback
      return `Edit ${varName}`;
    },
    [formData]
  );

  const extractVariables = useCallback(
    (html: string, config?: Record<string, any>): Variable[] => {
      const regex = /{{=\s*(\w+(?:\.\w+)*)\s*}}/g;
      const uniqueVars = new Set<string>();
      let match;

      while ((match = regex.exec(html)) !== null) {
        uniqueVars.add(match[1]);
      }

      return Array.from(uniqueVars).map((name) => ({
        name,
        value: config?.[name] || getDefaultValueForVariable(name),
      }));
    },
    [getDefaultValueForVariable]
  );

  const renderTemplate = useCallback(
    (template: string, data: Record<string, any>) => {
      try {
        console.log("Rendering template with data:", data);
        console.log("Template snippet:", template);

        const rendered = (eta as any).renderString(template, data);
        console.log(
          "Rendered HTML snippet:",
          rendered.substring(0, 200) + "..."
        );

        setRenderedHtml(rendered);
        setError(null);
      } catch (err) {
        console.error("Template rendering error:", err);
        setError(
          `Failed to render template: ${
            err instanceof Error ? err.message : String(err)
          }`
        );
      }
    },
    []
  );

  const generateAIContent = useCallback(
    async (isModification = false) => {
      setError(null);

      try {
        const requestData: AIBuildRequest = {
          prompt: isModification ? modificationRequest : formData.prompt,
          title: formData.title,
          description: formData.description,
          styles: {
            primaryColor: formData.primaryColor,
            secondaryColor: formData.secondaryColor,
            backgroundColor: formData.backgroundColor,
            textColor: formData.textColor,
            fontFamily: formData.fontFamily,
          },
          sections: ["hero", "about", "services", "contact"],
          businessInfo: {
            name: formData.businessName,
            industry: formData.industry,
            phone: formData.phone,
            email: formData.email,
            address: formData.address,
          },
          ...(isModification && {
            context: {
              previousHtml: htmlContent,
              modificationRequest: modificationRequest,
              variables: variables.reduce(
                (acc, v) => ({ ...acc, [v.name]: v.value }),
                {}
              ),
            },
          }),
        };

        const data = await buildWithAI(requestData).unwrap();
        const html = data.html || "";
        const config = data.config || {};

        setHtmlContent(html);
        const extractedVars = extractVariables(html, config);
        setVariables(extractedVars);

        // Render with extracted variables
        const variableData = extractedVars.reduce((acc, variable) => {
          const keys = variable.name.split(".");
          if (keys.length > 1) {
            if (!acc[keys[0]]) acc[keys[0]] = {};
            acc[keys[0]][keys[1]] = variable.value;
          } else {
            acc[variable.name] = variable.value;
          }
          return acc;
        }, {} as Record<string, any>);

        renderTemplate(html, variableData);
        setHasVariableChanges(false);

        if (!isModification) {
          setActiveTab("variables");
        } else {
          setModificationRequest("");
        }
      } catch (err: any) {
        setError(err.data?.message || err.message || "An error occurred");
      }
    },
    [
      buildWithAI,
      renderTemplate,
      formData,
      htmlContent,
      variables,
      modificationRequest,
      extractVariables,
    ]
  );

  const handleVariableChange = useCallback(
    (variableName: string, value: string) => {
      setVariables((prev) => {
        const updated = prev.map((v) =>
          v.name === variableName ? { ...v, value } : v
        );
        setHasVariableChanges(true);
        return updated;
      });
    },
    []
  );

  const applyVariableChanges = useCallback(() => {
    if (htmlContent && variables.length > 0) {
      const variableData = variables.reduce((acc, variable) => {
        const keys = variable.name.split(".");
        if (keys.length > 1) {
          if (!acc[keys[0]]) acc[keys[0]] = {};
          acc[keys[0]][keys[1]] = variable.value;
        } else {
          acc[variable.name] = variable.value;
        }
        return acc;
      }, {} as Record<string, any>);

      renderTemplate(htmlContent, variableData);
      setHasVariableChanges(false);
    }
  }, [htmlContent, variables, renderTemplate]);

  const handleFormChange = (field: keyof FormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleModify = () => {
    if (modificationRequest.trim()) {
      generateAIContent(true);
    }
  };

  const renderForm = () => (
    <FormSection>
      <FormGroup>
        <FormLabel>Prompt</FormLabel>
        <FormTextarea
          value={formData.prompt}
          onChange={(e) => handleFormChange("prompt", e.target.value)}
          placeholder="Describe what kind of landing page you want..."
        />
      </FormGroup>

      <FormGroup>
        <FormLabel>Title</FormLabel>
        <FormInput
          value={formData.title}
          onChange={(e) => handleFormChange("title", e.target.value)}
          placeholder="Page title"
        />
      </FormGroup>

      <FormGroup>
        <FormLabel>Description</FormLabel>
        <FormTextarea
          value={formData.description}
          onChange={(e) => handleFormChange("description", e.target.value)}
          placeholder="Brief description of your business"
        />
      </FormGroup>

      <PanelTitle style={{ fontSize: "16px", marginTop: "20px" }}>
        Colors
      </PanelTitle>
      <ColorsRow>
        <FormGroup>
          <FormLabel>Primary Color</FormLabel>
          <ColorInput
            type="color"
            value={formData.primaryColor}
            onChange={(e) => handleFormChange("primaryColor", e.target.value)}
          />
        </FormGroup>
        <FormGroup>
          <FormLabel>Secondary Color</FormLabel>
          <ColorInput
            type="color"
            value={formData.secondaryColor}
            onChange={(e) => handleFormChange("secondaryColor", e.target.value)}
          />
        </FormGroup>
      </ColorsRow>

      <PanelTitle style={{ fontSize: "16px", marginTop: "20px" }}>
        Business Info
      </PanelTitle>
      <FormGroup>
        <FormLabel>Business Name</FormLabel>
        <FormInput
          value={formData.businessName}
          onChange={(e) => handleFormChange("businessName", e.target.value)}
          placeholder="Your business name"
        />
      </FormGroup>

      <FormGroup>
        <FormLabel>Industry</FormLabel>
        <FormInput
          value={formData.industry}
          onChange={(e) => handleFormChange("industry", e.target.value)}
          placeholder="e.g., Technology, Healthcare"
        />
      </FormGroup>

      <GenerateButton
        onClick={() => generateAIContent(false)}
        disabled={isLoading}
      >
        {isLoading ? "Generating..." : "Generate AI Content"}
      </GenerateButton>
    </FormSection>
  );

  const renderVariables = () => (
    <FormSection>
      {variables.length === 0 ? (
        <div style={{ textAlign: "center", color: "#666", padding: "20px" }}>
          Generate content first to see editable variables
        </div>
      ) : (
        <>
          {variables.map((variable) => {
            const isColorVariable =
              variable.name.toLowerCase().includes("color") ||
              variable.name.toLowerCase().includes("bg");

            return (
              <VariableGroup key={variable.name}>
                <FormLabel>
                  {variable.name
                    .replace(/([A-Z])/g, " $1")
                    .replace(/^./, (str) => str.toUpperCase())}
                </FormLabel>
                {isColorVariable ? (
                  <ColorInput
                    type="color"
                    value={variable.value || "#000000"}
                    onChange={(e) =>
                      handleVariableChange(variable.name, e.target.value)
                    }
                  />
                ) : (
                  <FormInput
                    type="text"
                    value={variable.value || ""}
                    onChange={(e) =>
                      handleVariableChange(variable.name, e.target.value)
                    }
                    placeholder={`Enter ${variable.name}...`}
                  />
                )}
              </VariableGroup>
            );
          })}
          {hasVariableChanges && (
            <ApplyButton onClick={applyVariableChanges}>
              Apply Changes
            </ApplyButton>
          )}
        </>
      )}
    </FormSection>
  );

  const renderModify = () => (
    <FormSection>
      {htmlContent ? (
        <>
          <FormGroup>
            <FormLabel>Modification Request</FormLabel>
            <FormTextarea
              value={modificationRequest}
              onChange={(e) => setModificationRequest(e.target.value)}
              placeholder="Describe what you want to change... e.g., 'Make the hero section more modern', 'Add testimonials section', 'Change the color scheme to blue'"
            />
          </FormGroup>
          <ModifyButton
            onClick={handleModify}
            disabled={isLoading || !modificationRequest.trim()}
          >
            {isLoading ? "Modifying..." : "Apply Modification"}
          </ModifyButton>
        </>
      ) : (
        <div style={{ textAlign: "center", color: "#666", padding: "20px" }}>
          Generate content first to make modifications
        </div>
      )}
    </FormSection>
  );

  return (
    <AIBuilderContainer>
      <LeftPanel>
        <PanelTitle>AI Builder</PanelTitle>

        <TabContainer>
          <TabButton
            active={activeTab === "form"}
            onClick={() => setActiveTab("form")}
          >
            Setup
          </TabButton>
          <TabButton
            active={activeTab === "variables"}
            onClick={() => setActiveTab("variables")}
          >
            Variables
          </TabButton>
          <TabButton
            active={activeTab === "modify"}
            onClick={() => setActiveTab("modify")}
          >
            Modify
          </TabButton>
        </TabContainer>

        {error && <ErrorContainer>{error}</ErrorContainer>}

        {activeTab === "form" && renderForm()}
        {activeTab === "variables" && renderVariables()}
        {activeTab === "modify" && renderModify()}
      </LeftPanel>

      <PreviewContainer>
        <PanelTitle>Preview</PanelTitle>
        {isLoading ? (
          <LoadingContainer>
            {activeTab === "modify"
              ? "Applying modifications..."
              : "Generating AI content..."}
          </LoadingContainer>
        ) : renderedHtml ? (
          <PreviewFrame
            srcDoc={renderedHtml}
            title="AI Generated Page Preview"
          />
        ) : (
          <LoadingContainer>
            Fill out the form and click "Generate AI Content" to start
          </LoadingContainer>
        )}
      </PreviewContainer>
    </AIBuilderContainer>
  );
};
